import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getResourceById, getRelatedResources } from "./resourcesData";
import SnackbarNotification from "../shared/SnackbarNotification/SnackbarNotification";

const ResourceDetailPage = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [relatedResources, setRelatedResources] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [currentRelatedIndex, setCurrentRelatedIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundResource = getResourceById(resourceId);
    if (foundResource) {
      setResource(foundResource);
      setRelatedResources(getRelatedResources(foundResource));
    } else {
      // Resource not found, redirect to resources page
      navigate("/resources");
    }
  }, [resourceId, navigate]);

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(
      () => {
        setSnackbarMessage("Resource link copied to clipboard!");
        setShowSnackbar(true);
      },
      (err) => {
        console.error("Could not copy text: ", err);
        setSnackbarMessage("Failed to copy link");
        setShowSnackbar(true);
      }
    );
  };

  const handleDownload = () => {
    navigate(`/resources/${resourceId}/download`);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const handleRelatedPrevious = () => {
    setCurrentRelatedIndex((prevIndex) =>
      prevIndex === 0 ? Math.max(0, relatedResources.length - 3) : prevIndex - 1
    );
  };

  const handleRelatedNext = () => {
    setCurrentRelatedIndex((prevIndex) =>
      prevIndex >= relatedResources.length - 3 ? 0 : prevIndex + 1
    );
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Resource Not Found
          </h1>
          <Link
            to="/resources"
            className="text-blue hover:text-blue-dark font-medium"
          >
            ← Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-[100px]">
      {/* Main Content */}
      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Link */}
          <div className="mb-6">
            <Link
              to="/resources"
              className="text-black hover:text-gray-600 flex items-center gap-2 text-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Text content */}
            <div>
              <h1 className="!text-[40px] font-bold text-orange mb-6 font-poppins text-left leading-tight">
                {resource.title}
              </h1>

              <p className="!text-[30px] text-black leading-relaxed mb-10 text-left">
                {resource.description}
              </p>

              <div className="flex flex-row gap-4">
                <button
                  onClick={handleCopyLink}
                  className="bg-blue-navy text-white px-8 py-2.5 rounded-full font-semibold hover:opacity-90 transition-opacity"
                >
                  Share
                </button>

                <button
                  onClick={handleDownload}
                  className="bg-blue text-white px-8 py-2.5 rounded-full font-semibold hover:bg-blue-dark1 transition-colors"
                >
                  Download Now
                </button>
              </div>
            </div>

            {/* Right side - Image */}
            <div className="flex items-center justify-center">
              <div className="w-full rounded-2xl overflow-hidden">
                <img
                  src={resource.image}
                  alt={resource.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="w-full h-64 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center"
                  style={{ display: "none" }}
                >
                  <svg
                    className="w-16 h-16 text-yellow-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Related Resources Section */}
          {relatedResources.length > 0 && (
            <div className="mt-32">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-blue">
                  Related Free Resources
                </h2>
                {relatedResources.length > 3 && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleRelatedPrevious}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleRelatedNext}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedResources
                  .slice(currentRelatedIndex, currentRelatedIndex + 3)
                  .map((relatedResource) => (
                    <Link
                      key={relatedResource.id}
                      to={`/resources/${relatedResource.id}`}
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
                        <img
                          src={relatedResource.image}
                          alt={relatedResource.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div
                          className="w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center"
                          style={{ display: "none" }}
                        >
                          <svg
                            className="w-16 h-16 text-blue-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="p-6">
                        <span className="text-orange text-sm font-medium">
                          {relatedResource.category}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900 mt-2 mb-3">
                          {relatedResource.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {relatedResource.description}
                        </p>
                        <span className="text-blue hover:text-blue-dark font-medium text-sm flex items-center gap-1">
                          Get the freebie
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      <SnackbarNotification
        isOpen={showSnackbar}
        message={snackbarMessage}
        type="success"
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default ResourceDetailPage;
